import React, { useState, useEffect } from 'react';
import {
  TaskyButton,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
} from '../tasky';

interface OrganizationMemberFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (memberData: { name: string; email: string; role: string; password?: string }) => void;
  organizationName?: string;
  initialData?: { name: string; email: string; role: string };
  isEdit?: boolean;
}

const OrganizationMemberFormModal: React.FC<OrganizationMemberFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  organizationName,
  initialData,
  isEdit = false
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // モーダルが開かれた時にフォームをリセットまたは初期値を設定
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setName(initialData.name);
        setEmail(initialData.email);
        setRole(initialData.role);
        setErrors({});
      } else {
        resetForm();
      }
    }
  }, [isOpen, initialData]);

  // フォームリセット
  const resetForm = () => {
    setName('');
    setEmail('');
    setRole('member');
    setPassword('');
    setErrors({});
  };

  // 入力検証
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = '名前を入力してください';
    }

    if (!email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!role) {
      newErrors.role = 'ロールを選択してください';
    }

    // 新規追加時、パスワードが入力されていれば8文字以上かチェック
    if (!isEdit && password && password.length < 8) {
      newErrors.password = 'パスワードは8文字以上で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フォーム送信処理
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const memberData: { name: string; email: string; role: string; password?: string } = { name, email, role };
    if (!isEdit && password) {
      memberData.password = password;
    }
    onSubmit(memberData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      title={isEdit ? 'メンバー編集' : 'メンバー追加'}
    >
      {organizationName && (
        <p className="text-sm text-gray-500 mb-4">
          組織「{organizationName}」{isEdit ? 'のメンバーを編集します' : 'にメンバーを追加します'}
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <TaskyFormGroup label="名前" htmlFor="name" error={errors.name}>
          <TaskyInput
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="メンバーの名前"
            hasError={Boolean(errors.name)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="メールアドレス"
          htmlFor="email"
          error={errors.email}
          helper={
            !isEdit
              ? '既存のユーザーのメールアドレスを入力してください。新規ユーザーの場合は招待メールが送信されます。'
              : undefined
          }
        >
          <TaskyInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="招待するユーザーのメールアドレス"
            disabled={isEdit}
            hasError={Boolean(errors.email)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="ロール"
          htmlFor="role"
          error={errors.role}
          helper="リーダーは組織の設定変更やメンバー管理が可能です。"
        >
          <TaskySelect
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            hasError={Boolean(errors.role)}
          >
            <option value="member">メンバー</option>
            <option value="leader">リーダー</option>
          </TaskySelect>
        </TaskyFormGroup>

        {!isEdit && (
          <TaskyFormGroup
            label="パスワード（任意）"
            htmlFor="password"
            error={errors.password}
            helper="空欄の場合、パスワードは自動生成されます。"
          >
            <TaskyInput
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8文字以上（空欄の場合は自動生成）"
              hasError={Boolean(errors.password)}
            />
          </TaskyFormGroup>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <TaskyButton type="button" variant="outline" size="sm" onClick={onClose}>
            キャンセル
          </TaskyButton>
          <TaskyButton type="submit" size="sm">
            {isEdit ? '更新' : 'メンバーを追加'}
          </TaskyButton>
        </div>
      </form>
    </TaskyDialog>
  );
};

export default OrganizationMemberFormModal;
